import { ID } from '@ianesthesie-kit/services';
import { doctorBffApi, InvalidateArr } from '../doctorBffApi.slice';
import { wrapError } from '../error';
import { BillingLineService } from './billingLine.service';
import {
  BillingDashboardFilters,
  BillingDashboardQuery,
  BillingLineDto,
  BillingLineEventDto,
  BillingLinePatchPayload,
  BillingPaymentDto,
  BillingReminderResultDto,
  BillingSummaryDto,
  BulkRemindPayload,
  ConfirmConsultationPayload,
  ConsultationSuggestionDto,
  ConsultationTariffEntryDto,
  DoctorBillingSettingsDto,
  DoctorBillingSettingsPayload,
  LogPhoneCallPayload,
  PagedBillingDashboard,
  RecordPaymentPayload,
  ReminderCadenceConfigDto,
  ReminderCadenceStepDto,
  WaivePayload
} from './billingLine.model';

type WithLineId<T = unknown> = T & { lineId: ID };

/**
 * Tag design: `BillingLines` (per patient id) covers the PDV line list; `BillingLineEvents` /
 * `BillingPayments` (per line id) cover a line's trail; `BillingDashboard` + `BillingSummary`
 * cover the hospital chase views; `BillingSettings` (per area id: tariff / cadence / doctor-…)
 * covers config AND the consultation suggestion, which is derived from tariff + doctor DH.
 * The suggestion additionally depends on the patient's ASA score, so it also declares
 * `SurgeryInfo` (per patient id) — the tag every ASA writer already invalidates.
 * Line mutations invalidate and refetch. Consultation confirmation also applies the returned
 * line immediately, so a quote cannot keep the previous doctor while a refetch is queued.
 */
const lineMutationInvalidates = (err: unknown, lineId?: ID): InvalidateArr => {
  if (err) {
    return [];
  }
  const arr: InvalidateArr = [
    'BillingLines',
    'BillingDashboard',
    'BillingSummary'
  ];
  if (lineId) {
    arr.push({ type: 'BillingLineEvents', id: lineId });
  } else {
    arr.push('BillingLineEvents');
  }
  return arr;
};

export const billingLineApi = doctorBffApi.injectEndpoints({
  overrideExisting: false,
  endpoints: (builder) => ({
    // -----------------------------------------------------------------------
    // PDV / line-scoped
    // -----------------------------------------------------------------------
    consultationLineSuggestion: builder.query<
      ConsultationSuggestionDto,
      { patientId: ID; doctorId?: ID }
    >({
      // The suggested code is ASA-derived (APC from ASA >= 3), so the suggestion is stale as
      // soon as the surgery info changes: depend on `SurgeryInfo` too, which every ASA writer
      // (`patchSurgeryInfo`, the MHO / meds / consultation ASA-prediction paths) invalidates.
      providesTags: (res, err, { patientId }) =>
        err ? [] : ['BillingSettings', { type: 'SurgeryInfo', id: patientId }],
      queryFn: ({ patientId, doctorId }) =>
        BillingLineService.suggestConsultationLine(patientId, doctorId)
          .then((data) => ({ data }))
          .catch(wrapError)
    }),

    confirmConsultationLine: builder.mutation<
      BillingLineDto,
      { patientId: ID } & ConfirmConsultationPayload
    >({
      invalidatesTags: (res, err) => lineMutationInvalidates(err, res?.id),
      async onQueryStarted({ patientId }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            billingLineApi.util.updateQueryData(
              'billingLines',
              { patientId },
              (lines) => {
                const index = lines.findIndex((line) => line.id === data.id);
                if (index < 0) {
                  lines.push(data);
                } else {
                  lines[index] = data;
                }
              }
            )
          );
        } catch {
          // A rejected confirmation keeps the existing line and its doctor.
        }
      },
      queryFn: ({ patientId, ...payload }) =>
        BillingLineService.confirmConsultationLine(patientId, payload)
          .then((data) => ({ data }))
          .catch(wrapError)
    }),

    billingLines: builder.query<BillingLineDto[], { patientId: ID }>({
      providesTags: (res, err, { patientId }) =>
        err ? [] : [{ type: 'BillingLines', id: patientId }, 'BillingLines'],
      queryFn: ({ patientId }) =>
        BillingLineService.fetchLinesByPatient(patientId)
          .then((data) => ({ data }))
          .catch(wrapError)
    }),

    patchBillingLine: builder.mutation<
      BillingLineDto,
      WithLineId<BillingLinePatchPayload>
    >({
      invalidatesTags: (res, err, { lineId }) =>
        lineMutationInvalidates(err, lineId),
      queryFn: ({ lineId, ...payload }) =>
        BillingLineService.patchLine(lineId, payload)
          .then((data) => ({ data }))
          .catch(wrapError)
    }),

    waiveBillingLine: builder.mutation<
      BillingLineDto,
      WithLineId<WaivePayload>
    >({
      invalidatesTags: (res, err, { lineId }) =>
        lineMutationInvalidates(err, lineId),
      queryFn: ({ lineId, ...payload }) =>
        BillingLineService.waiveLine(lineId, payload)
          .then((data) => ({ data }))
          .catch(wrapError)
    }),

    unwaiveBillingLine: builder.mutation<BillingLineDto, WithLineId>({
      invalidatesTags: (res, err, { lineId }) =>
        lineMutationInvalidates(err, lineId),
      queryFn: ({ lineId }) =>
        BillingLineService.unwaiveLine(lineId)
          .then((data) => ({ data }))
          .catch(wrapError)
    }),

    billingLineEvents: builder.query<BillingLineEventDto[], WithLineId>({
      providesTags: (res, err, { lineId }) =>
        err ? [] : [{ type: 'BillingLineEvents', id: lineId }],
      queryFn: ({ lineId }) =>
        BillingLineService.fetchLineEvents(lineId)
          .then((data) => ({ data }))
          .catch(wrapError)
    }),

    recordBillingPayment: builder.mutation<
      BillingLineDto,
      WithLineId<RecordPaymentPayload>
    >({
      invalidatesTags: (res, err, { lineId }) =>
        err
          ? []
          : [
              ...lineMutationInvalidates(err, lineId),
              { type: 'BillingPayments', id: lineId }
            ],
      queryFn: ({ lineId, ...payload }) =>
        BillingLineService.recordPayment(lineId, payload)
          .then((data) => ({ data }))
          .catch(wrapError)
    }),

    markBillingLinePaidExternally: builder.mutation<BillingLineDto, WithLineId>(
      {
        invalidatesTags: (res, err, { lineId }) =>
          err
            ? []
            : [
                ...lineMutationInvalidates(err, lineId),
                { type: 'BillingPayments', id: lineId }
              ],
        queryFn: ({ lineId }) =>
          BillingLineService.markPaidExternally(lineId)
            .then((data) => ({ data }))
            .catch(wrapError)
      }
    ),

    billingLinePayments: builder.query<BillingPaymentDto[], WithLineId>({
      providesTags: (res, err, { lineId }) =>
        err ? [] : [{ type: 'BillingPayments', id: lineId }],
      queryFn: ({ lineId }) =>
        BillingLineService.fetchLinePayments(lineId)
          .then((data) => ({ data }))
          .catch(wrapError)
    }),

    remindBillingLine: builder.mutation<BillingLineDto, WithLineId>({
      invalidatesTags: (res, err, { lineId }) =>
        lineMutationInvalidates(err, lineId),
      queryFn: ({ lineId }) =>
        BillingLineService.remindLine(lineId)
          .then((data) => ({ data }))
          .catch(wrapError)
    }),

    logBillingPhoneCall: builder.mutation<
      BillingLineDto,
      WithLineId<LogPhoneCallPayload>
    >({
      invalidatesTags: (res, err, { lineId }) =>
        lineMutationInvalidates(err, lineId),
      queryFn: ({ lineId, ...payload }) =>
        BillingLineService.logPhoneCall(lineId, payload)
          .then((data) => ({ data }))
          .catch(wrapError)
    }),

    // -----------------------------------------------------------------------
    // Dashboard / hospital-scoped
    // -----------------------------------------------------------------------
    billingDashboard: builder.query<
      PagedBillingDashboard,
      BillingDashboardQuery
    >({
      providesTags: ['BillingDashboard'],
      queryFn: (query) =>
        BillingLineService.fetchDashboard(query)
          .then((data) => ({ data }))
          .catch(wrapError)
    }),

    billingSummary: builder.query<BillingSummaryDto, BillingDashboardFilters>({
      providesTags: ['BillingSummary'],
      queryFn: (filters) =>
        BillingLineService.fetchSummary(filters)
          .then((data) => ({ data }))
          .catch(wrapError)
    }),

    remindBillingLines: builder.mutation<
      BillingReminderResultDto[],
      BulkRemindPayload
    >({
      invalidatesTags: (res, err) => lineMutationInvalidates(err),
      queryFn: (payload) =>
        BillingLineService.remindLines(payload)
          .then((data) => ({ data }))
          .catch(wrapError)
    }),

    // -----------------------------------------------------------------------
    // Settings
    // -----------------------------------------------------------------------
    consultationTariff: builder.query<ConsultationTariffEntryDto[], void>({
      providesTags: [
        { type: 'BillingSettings', id: 'tariff' },
        'BillingSettings'
      ],
      queryFn: () =>
        BillingLineService.fetchConsultationTariff()
          .then((data) => ({ data }))
          .catch(wrapError)
    }),

    upsertConsultationTariff: builder.mutation<
      ConsultationTariffEntryDto[],
      ConsultationTariffEntryDto[]
    >({
      invalidatesTags: (res, err) => (err ? [] : ['BillingSettings']),
      queryFn: (entries) =>
        BillingLineService.upsertConsultationTariff(entries)
          .then((data) => ({ data }))
          .catch(wrapError)
    }),

    doctorBillingSettings: builder.query<
      DoctorBillingSettingsDto,
      { doctorId?: ID } | void
    >({
      providesTags: (res, err, arg) =>
        err
          ? []
          : [
              {
                type: 'BillingSettings',
                id: `doctor-${arg?.doctorId ?? 'me'}`
              },
              'BillingSettings'
            ],
      queryFn: (arg) =>
        BillingLineService.fetchDoctorSettings(arg?.doctorId)
          .then((data) => ({ data }))
          .catch(wrapError)
    }),

    upsertDoctorBillingSettings: builder.mutation<
      DoctorBillingSettingsDto,
      DoctorBillingSettingsPayload & { doctorId?: ID }
    >({
      invalidatesTags: (res, err) => (err ? [] : ['BillingSettings']),
      queryFn: ({ doctorId, ...payload }) =>
        BillingLineService.upsertDoctorSettings(payload, doctorId)
          .then((data) => ({ data }))
          .catch(wrapError)
    }),

    reminderCadence: builder.query<ReminderCadenceConfigDto, void>({
      providesTags: [
        { type: 'BillingSettings', id: 'cadence' },
        'BillingSettings'
      ],
      queryFn: () =>
        BillingLineService.fetchReminderCadence()
          .then((data) => ({ data }))
          .catch(wrapError)
    }),

    replaceReminderCadence: builder.mutation<
      ReminderCadenceConfigDto,
      ReminderCadenceStepDto[]
    >({
      invalidatesTags: (res, err) => (err ? [] : ['BillingSettings']),
      queryFn: (steps) =>
        BillingLineService.replaceReminderCadence(steps)
          .then((data) => ({ data }))
          .catch(wrapError)
    })
  })
});

export const {
  useConsultationLineSuggestionQuery,
  useLazyConsultationLineSuggestionQuery,
  useConfirmConsultationLineMutation,
  useBillingLinesQuery,
  usePatchBillingLineMutation,
  useWaiveBillingLineMutation,
  useUnwaiveBillingLineMutation,
  useBillingLineEventsQuery,
  useLazyBillingLineEventsQuery,
  useRecordBillingPaymentMutation,
  useMarkBillingLinePaidExternallyMutation,
  useBillingLinePaymentsQuery,
  useLazyBillingLinePaymentsQuery,
  useRemindBillingLineMutation,
  useLogBillingPhoneCallMutation,
  useBillingDashboardQuery,
  useBillingSummaryQuery,
  useRemindBillingLinesMutation,
  useConsultationTariffQuery,
  useUpsertConsultationTariffMutation,
  useDoctorBillingSettingsQuery,
  useUpsertDoctorBillingSettingsMutation,
  useReminderCadenceQuery,
  useReplaceReminderCadenceMutation
} = billingLineApi;
